import { useState } from 'react'
import { IconDownload } from '@tabler/icons-react'
import { ProductDownload, ProductOverview } from 'shared-types'

import { Link } from '~/components'
import { Image } from '~/components/Image'
import RichText from '~/components/RichText/RichText'
import Typography, { TypographyTag, TypographyVariant } from '~/components/Typography'
import { convertProductResourceUrl, getImageSrc } from '~/helpers'
import { usePage } from '~/hooks'
import { HtmlContent } from '../../HtmlContent'
import { ProductDashboardHtmlContentItem } from '../ProductDashboard.types'
import { TabGroups } from '../TabGroups'
import { TabGroupOption, TabGroupsSelector } from '../TabGroups/TabGroupsSelector'
import styles from './ProductDashboardSummary.module.scss'

export interface ProductDashboardSummaryProps {
  downloads?: ProductDownload[]
  overview?: ProductOverview
  overviewHtmlContent?: ProductDashboardHtmlContentItem[]
}

export const ProductDashboardSummary = ({
  downloads,
  overview,
  overviewHtmlContent,
}: ProductDashboardSummaryProps) => {
  const tabs: TabGroupOption[] = [
    { id: 'overview', name: 'Overview' },
  ]

  const { content, icons } = overview
  const { siteCode } = usePage()

  const [selectedTabId, setSelectedTabId] = useState<string>(tabs[0].id)

  if (downloads?.length) {
    tabs.push({ id: 'downloads', name: 'Downloads' })
  }

  return (
    <div className={styles.root}>
      <TabGroups>
        <TabGroupsSelector
          selectedTabId={selectedTabId}
          setSelectedTabId={setSelectedTabId}
          tabs={tabs}
        />
        <div className={styles.content}>
          {selectedTabId === 'overview' && (
            <div className={styles.overview}>
              {icons?.length > 0 && (
                <div className={styles.icons}>
                  {icons.map((icon, index) => {
                    return (
                      <Link key={index} href={icon.href} className='block relative'>
                        <Image
                          src={getImageSrc(icon.src, '200')}
                          width={icon.width}
                          height={icon.height}
                          alt={icon.altText}
                          title={icon.altText}
                        />
                      </Link>
                    )
                  })}
                </div>
              )}
              {content && <RichText content={content} />}
              {!!overviewHtmlContent?.length && (
                <div className={styles.customHtmlContent}>
                  {overviewHtmlContent.map((overviewHtmlContentItem, index) => {
                    return (
                      <HtmlContent
                        key={overviewHtmlContentItem.id || index}
                        contentId={overviewHtmlContentItem.id || ''}
                        content={overviewHtmlContentItem.content || ''}
                        css={overviewHtmlContentItem.css}
                        javascript={overviewHtmlContentItem.javascript}
                        isPageSection={false}
                      />
                    )
                  })}
                </div>
              )}
            </div>
          )}

          {selectedTabId === 'downloads' && (
            <ul className={styles.downloads}>
              {downloads?.map((download, index) => {
                return (
                  <li key={index} className={styles.downloadItem}>
                    <Link href={convertProductResourceUrl(download.link, siteCode)} className={styles.downloadLink} target='_blank' rel='noopener noreferrer'>
                      <div className={styles.downloadIconBox}>
                        <IconDownload size={16} color='var(--color-pdp-download-color)' />
                      </div>
                      <Typography variant={TypographyVariant.BodyRegularMedium} tag={TypographyTag.span} className={styles.downloadTitle}>{download.title}</Typography>
                    </Link>
                  </li>
                )
              })}
            </ul>
          )}
        </div>
      </TabGroups>
    </div>
  )
}
